import React from 'react';
import IEvaluationFormStep from 'interfaces/IEvaluationFormStep';
import { Form, FormControl, FormGroup, Row } from 'react-bootstrap';
import { IPartData, IPartDData } from 'interfaces/IApplicationReviewForm';
import HelpButton from '../../../Form/HelpButton';

const PartD: React.FC<IEvaluationFormStep> = ({
    requirements,
    formData,
    requirementsGroup,
    formRef,
    onFeedback,
    onInputChange,
    readOnly = false,
}) => {
    const partValue: IPartData<IPartDData> = formData.partD || {};
    const help = requirements.config?.help && requirements.config?.help[requirementsGroup.name];

    const [showValidation, setShowValidation] = React.useState(false);

    const handleChange = (name: string, value: string | number) => {
        onInputChange('partD', {
            ...partValue,
            value: {
                ...partValue.value,
                [name]: value,
            },
        });
    };

    const handleChangeComplete = (value: boolean) => {
        setShowValidation(true);

        if (value) {
            const result = formRef.current.checkValidity();
            if (!result) {
                onFeedback('danger', 'Cannot mark as complete whilst form has errors');
                return;
            }
        }

        onInputChange('partD', {
            ...partValue,
            complete: value,
        });
    };

    const renderHoursInput = (name: string, labelText: string) => {
        const inputValue = partValue?.value && partValue.value[name];
        const valid = inputValue !== undefined && (inputValue as number) >= 0;
        const invalid =
            inputValue === undefined || (inputValue as string) === '' || (inputValue as number) < 0;

        return (
            <React.Fragment key={name}>
                <dt className={'col-sm-2'}>
                    {!readOnly && (
                        <Form.Control
                            name={name}
                            plaintext={readOnly}
                            type={'number'}
                            readOnly={readOnly || partValue?.complete}
                            onChange={(e) => handleChange(name, e.target.value)}
                            value={inputValue || ''}
                            min={0}
                            required
                            isValid={showValidation && valid}
                            isInvalid={showValidation && invalid}
                        />
                    )}
                    {readOnly && inputValue}
                </dt>
                <dd className={'col-sm-10'}>{labelText}</dd>
            </React.Fragment>
        );
    };

    return (
        <Row>
            <h2>
                {requirementsGroup.pageTitle}{' '}
                {partValue?.complete && (
                    <i
                        className={'bi bi-check2-circle me-2 fs-4'}
                        title={`${requirementsGroup.pageTitle} complete`}
                    />
                )}
                <HelpButton title={requirementsGroup.pageTitle} content={help} />
            </h2>
            <p className={'text-black'}>{requirementsGroup.pageSubtitle}</p>
            <Row className={'text-black fw-bold'}>
                <p>How many hours of supervision have taken place?</p>
            </Row>
            <Row>
                <dl className={'row'}>
                    {requirementsGroup.requirements
                        .filter((req) => req.name.startsWith('hours'))
                        .map((req) => renderHoursInput(req.name, req.subtitle))}
                </dl>
            </Row>

            {requirementsGroup.requirements
                .filter((req) => !req.name.startsWith('hours'))
                .map((req) => {
                    const reqValue = partValue?.value && partValue?.value[req.name];
                    const reqValueString = (reqValue as string) || '';
                    const minLength = req.minLength ?? 1;
                    const valid =
                        reqValue !== undefined &&
                        reqValueString.length >= minLength &&
                        reqValueString.length <= (req.maxLength ?? Number.MAX_SAFE_INTEGER);
                    const invalid =
                        reqValue !== undefined &&
                        (reqValueString.length < minLength ||
                            reqValueString.length > (req.maxLength ?? Number.MAX_SAFE_INTEGER));

                    return (
                        <FormGroup className={'text-black'} key={req.name}>
                            <Form.Label>
                                <p className={'fw-bold'}>{req.subtitle}</p>
                                <FormControl
                                    as={'textarea'}
                                    rows={10}
                                    plaintext={readOnly}
                                    className="mt-2"
                                    required
                                    value={reqValueString || ''}
                                    name={req.name}
                                    readOnly={readOnly || partValue?.complete}
                                    onChange={(e) => handleChange(req.name, e.target.value)}
                                    minLength={minLength}
                                    isValid={showValidation && valid}
                                    isInvalid={showValidation && invalid}
                                />
                            </Form.Label>
                            {!readOnly && (
                                <div>
                                    {minLength > 0 && reqValueString.length < minLength && (
                                        <Form.Text className={'flex-grow-0'}>
                                            Enter at least {minLength} characters
                                        </Form.Text>
                                    )}
                                    {req.maxLength && reqValueString.length > req.maxLength && (
                                        <Form.Text className={'flex-grow-0 text-danger'}>
                                            Too many characters
                                        </Form.Text>
                                    )}
                                    {req.maxLength && (
                                        <Form.Text
                                            className={`flex-grow-0 float-end text-end ${(reqValueString.length || 0) > req.maxLength && 'text-danger'}`}
                                        >
                                            {reqValueString.length || 0}/{req.maxLength}
                                        </Form.Text>
                                    )}
                                </div>
                            )}
                        </FormGroup>
                    );
                })}

            {!readOnly && (
                <div className={'mt-1 w-25'}>
                    <Form.Label className={'d-flex'}>
                        <Form.Check
                            checked={partValue.complete}
                            // disabled={!valid && !partValue.complete}
                            title={`Mark section as ${partValue.complete ? 'in' : ''}complete`}
                            onChange={() => handleChangeComplete(!partValue.complete)}
                            className={'me-2'}
                        />
                        Section is complete
                    </Form.Label>
                    <FormControl type={'hidden'} value={partValue.complete ? 'true' : 'false'} />
                </div>
            )}
        </Row>
    );
};

export default PartD;
